import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as FramerMotion from 'framer-motion';

import Icon from '../Icon';
import { ChevronIcon } from 'assets/icon';
import { LegacyColors } from 'constants';
import Button from '../Button';

const PANEL_TYPES = ['default', 'faq', 'claim'];

const ExpansionPanel = ({
  className,
  header,
  children,
  onChange,
  expanded: expandedProp,
  headerStyle,
  id,
  hr = true,
  type = 'default',
  dataDdActionName,
  dataDdCloseActionName,
  contentClassName,
}) => {
  const isClaim = type === 'claim';
  const isFaq = type === 'faq';
  const controlled = !!onChange;

  const [expandedState, setExpanded] = useState(false);
  const [divFocus, setDivFocus] = useState(false);
  const divRef = useRef(null);

  const expanded = controlled ? expandedProp : expandedState;

  const handleChange = (e) =>
    controlled ? onChange(e, !expanded) : setExpanded(!expanded);

  const handleKeyUp = (e) => {
    if (
      e &&
      ((e.shiftKey && e.keyCode === 9) || e.keyCode === 9 || e.keyCode === 13)
    ) {
      handleChange(e);
    }
  };
  const { motion } = FramerMotion;

  const summaryInteractionProps = isClaim
    ? { style: { ...headerStyle, cursor: 'default' } }
    : {
        role: 'button',
        tabIndex: '0',
        onFocus: () => setDivFocus(true),
        onBlur: () => setDivFocus(false),
        onClick: handleChange,
        onKeyUp: handleKeyUp,
        style: headerStyle,
      };

  const showFocusRing = divFocus && !isClaim;

  return (
    <div
      className={clsx(
        'w-full relative bg-legacy-white text-[rgba(0,0,0,0.87)] transition-[margin] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]',
        !isFaq && 'rounded-tl rounded-tr',
        '[&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:-top-px [&:not(:first-child)]:before:inset-x-0 [&:not(:first-child)]:before:h-px [&:not(:first-child)]:before:bg-[rgba(0,0,0,0.12)] [&:not(:first-child)]:before:content-[""]',
        isFaq && 'hover:bg-legacy-bgLight [&.expanded]:bg-legacy-bgLight',
        className,
        expanded && 'expanded',
        showFocusRing &&
          (isFaq
            ? 'shadow-none m-0 p-0'
            : 'outline-none my-[3px] px-[3px] shadow-[0px_0px_6px_1px_theme(colors.legacy.primary)]'),
      )}
    >
      <div
        id={id}
        data-testid={id}
        data-dd-action-name={
          expanded ? dataDdCloseActionName : dataDdActionName
        }
        className={clsx(
          !isFaq && 'pr-0',
          expanded && 'min-h-[64px]',
          !isFaq &&
            'md:border-0 md:outline-none md:flex md:min-h-[48px] md:cursor-pointer md:transition-[min-height] md:duration-150 md:ease-[cubic-bezier(0.4,0,0.2,1)]',
          isFaq &&
            'border-0 outline-none flex items-center min-h-[48px] cursor-pointer transition-[min-height] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] px-[18px]',
          isFaq && expanded && 'border-b border-gray-300',
        )}
        aria-controls={children.props.id}
        aria-expanded={!!expanded}
        {...summaryInteractionProps}
      >
        <div
          className={clsx(
            !isFaq &&
              'md:flex-grow md:text-[0.9375rem] md:font-normal md:transition-[margin] md:duration-150 md:ease-[cubic-bezier(0.4,0,0.2,1)] md:w-min md:pr-[5px]',
            isFaq &&
              'my-[18px] min-w-0 flex-1 transition-[margin] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] pr-[15px]',
            contentClassName,
          )}
        >
          {header}
        </div>
        {isClaim ? (
          <div className="expand-icon transition-transform duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] -mr-3 shrink-0 py-[17px] px-3 overflow-visible text-center rounded-full">
            <Button
              id="expansion-button"
              data-dd-action-name={
                expanded
                  ? 'dashboard.claims.hide_details'
                  : 'dashboard.claims.view_details'
              }
              aria-label="Toggle group"
              style={{ margin: 'auto' }}
              type="secondary"
              onClick={handleChange}
            >
              View Details
              <ChevronIcon
                direction={expanded ? 'up' : 'down'}
                className="icon self-center ml-2"
                aria-label="Toggle detail"
              />
            </Button>
          </div>
        ) : (
          <Icon
            alt="Toggle group"
            name={expanded ? 'chevronUp' : 'chevronDown'}
            fontSize={14}
            color={LegacyColors.primary}
            className={isFaq ? 'mt-0 shrink-0' : undefined}
            onClick={handleChange}
          />
        )}
      </div>
      {isClaim && (hr || expanded) ? <hr className="mt-[16px]" /> : null}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{
          height: expanded ? 'auto' : 0,
          opacity: expanded ? 1 : 0,
        }}
        transition={{ duration: 0.06, ease: [0.4, 0, 0.2, 1] }}
        style={{
          overflow: 'hidden',
          padding: !isClaim && expanded ? '17px 24px' : '0',
        }}
      >
        <div ref={divRef}>{children}</div>
      </motion.div>
    </div>
  );
};

ExpansionPanel.propTypes = {
  className: PropTypes.string,
  expanded: PropTypes.bool,
  onChange: PropTypes.func,
  header: PropTypes.any,
  children: PropTypes.any,
  headerStyle: PropTypes.object,
  type: PropTypes.oneOf(PANEL_TYPES),
  dataDdActionName: PropTypes.string,
  dataDdCloseActionName: PropTypes.string,
};

export default ExpansionPanel;
